import { useEffect, useMemo, useState } from "react";
import { CalendarDays, List } from "lucide-react";
import api from "@/lib/api";
import { CATEGORIES } from "@/lib/constants";
import { EventCard } from "@/components/EventCard";
import { CATEGORY_COLORS } from "@/components/CategoryIcon";

function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("list");
  const [category, setCategory] = useState("");
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    api.get("/events", { params }).then((r) => setEvents(r.data));
  }, [category]);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const key = new Date(e.start_datetime).toDateString();
      (map[key] = map[key] || []).push(e);
    });
    return map;
  }, [events]);

  const days = monthGrid(cursor.getFullYear(), cursor.getMonth());
  const monthLabel = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="px-4 py-6 md:px-8">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">CSRA</span>
          <h1 className="font-serif text-3xl font-bold md:text-4xl">Events Calendar</h1>
        </div>
        <div className="flex rounded-full border border-border bg-card p-1">
          <button onClick={() => setView("list")} data-testid="events-list-view"
            className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <List size={15} /> List
          </button>
          <button onClick={() => setView("calendar")} data-testid="events-calendar-view"
            className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium ${view === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
            <CalendarDays size={15} /> Calendar
          </button>
        </div>
      </div>

      <div className="no-scrollbar mb-6 flex gap-2 overflow-x-auto pb-2">
        <button onClick={() => setCategory("")} className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-medium ${!category ? "bg-foreground text-background" : "border border-border bg-card"}`}>All</button>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCategory(c === category ? "" : c)}
            className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-medium ${category === c ? "text-white" : "border border-border bg-card"}`}
            style={category === c ? { backgroundColor: CATEGORY_COLORS[c] } : {}}>{c}</button>
        ))}
      </div>

      {view === "list" ? (
        events.length ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {events.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        ) : <div className="py-20 text-center text-muted-foreground">No upcoming events.</div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-4 flex items-center justify-between">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="rounded-full px-3 py-1 hover:bg-muted">‹</button>
            <span className="font-serif text-xl font-semibold">{monthLabel}</span>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="rounded-full px-3 py-1 hover:bg-muted">›</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-muted-foreground">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const dayEvents = byDay[d.toDateString()] || [];
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div key={i} className={`min-h-[74px] rounded-lg border p-1 ${inMonth ? "border-border" : "border-transparent opacity-40"} ${isToday ? "ring-2 ring-primary" : ""}`}>
                  <span className="text-xs font-medium">{d.getDate()}</span>
                  <div className="mt-0.5 space-y-0.5">
                    {dayEvents.slice(0, 2).map((e) => (
                      <a key={e.id} href={`/event/${e.id}`} className="block truncate rounded px-1 text-[9px] font-medium text-white"
                        style={{ backgroundColor: CATEGORY_COLORS[e.category] }}>{e.title}</a>
                    ))}
                    {dayEvents.length > 2 && <span className="text-[9px] text-muted-foreground">+{dayEvents.length - 2}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
